import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, LogOut, Plus, Edit, Trash2, UserPlus, ArrowRight } from 'lucide-react';

// Extracted sub-components
import FieldManager from '../components/admin/FieldManager';
import InvoicesTab from '../components/admin/InvoicesTab';
import VisitorsTab from '../components/admin/VisitorsTab';
import CommissionsTab from '../components/admin/CommissionsTab';
import EmailTab from '../components/admin/EmailTab';
import ExpandableEntityCard from '../components/admin/ExpandableEntityCard';
import { LeadModal, ProspectModal, CustomerModal, JobModal, CallbackModal, TemplateModal } from '../components/admin/Modals';
import { API } from '../components/admin/apiClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [commissionChartData, setCommissionChartData] = useState({ monthly: [], yearly: [] });
  const [invoices, setInvoices] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showProspectModal, setShowProspectModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editingProspect, setEditingProspect] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editingCallback, setEditingCallback] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingSalesRep, setEditingSalesRep] = useState(null);
  const [selectedEntitiesForEmail, setSelectedEntitiesForEmail] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, leadsRes, prospectsRes, customersRes, jobsRes, contactsRes, chatsRes, callbacksRes, templatesRes, fieldsRes, sectionsRes, salesRepsRes, commissionsRes, tiersRes, summaryRes, emailLogsRes, emailTemplatesRes, chartDataRes, invoicesRes, invoiceSummaryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/leads`),
        axios.get(`${API}/admin/prospects`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/jobs`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/chats`),
        axios.get(`${API}/admin/callbacks`),
        axios.get(`${API}/admin/templates`),
        axios.get(`${API}/admin/custom-fields`),
        axios.get(`${API}/admin/custom-sections`),
        axios.get(`${API}/admin/sales-reps`),
        axios.get(`${API}/admin/commissions`),
        axios.get(`${API}/admin/commission-tiers`),
        axios.get(`${API}/admin/commission-summary`),
        axios.get(`${API}/admin/email/logs`),
        axios.get(`${API}/admin/email/templates`),
        axios.get(`${API}/admin/commission-chart-data`),
        axios.get(`${API}/admin/invoices`),
        axios.get(`${API}/admin/invoices/summary/stats`)
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setProspects(prospectsRes.data);
      setCustomers(customersRes.data);
      setJobs(jobsRes.data);
      setContacts(contactsRes.data);
      setChats(chatsRes.data);
      setCallbacks(callbacksRes.data);
      setTemplates(templatesRes.data);
      setCustomFields(fieldsRes.data);
      setCustomSections(sectionsRes.data);
      setSalesReps(salesRepsRes.data);
      setCommissions(commissionsRes.data);
      setCommissionTiers(tiersRes.data);
      setCommissionSummary(summaryRes.data);
      setEmailLogs(emailLogsRes.data);
      setEmailTemplates(emailTemplatesRes.data);
      setCommissionChartData(chartDataRes.data);
      setInvoices(invoicesRes.data);
      setInvoiceSummary(invoiceSummaryRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error:', error);
      if (error.response?.status === 401) { navigate('/admin'); }
    } finally { setIsLoading(false); }
  }, [navigate]);

  // Auth: check sessionStorage token first (cross-origin), then cookie via /api/auth/me
  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('adminToken');
      if (token) {
        // Token exists in session — interceptor will attach it automatically
        setAuthChecked(true);
        fetchData();
        return;
      }
      // Fallback: check httpOnly cookie via /api/auth/me (same-origin only)
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (res.data.authenticated) {
          sessionStorage.setItem('userRole', res.data.role);
          sessionStorage.setItem('userId', res.data.user_id);
          sessionStorage.setItem('userName', res.data.user_name);
          setAuthChecked(true);
          fetchData();
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Auth check failed:', e);
        navigate('/admin');
      }
    };
    checkAuth();
  }, [navigate, fetchData]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Logout error:', e);
    }
    sessionStorage.clear();
    navigate('/admin');
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${id}`, {});
      fetchData();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  const handleConvert = async (fromType, id, toType) => {
    const msg = toType === 'prospect' ? 'Convert to Prospect?' : 'Convert to Customer? They will get a C1000+ ID.';
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/admin/${fromType}s/${id}/convert-to-${toType}`, {}, {});
      fetchData();
      if (toType === 'prospect') setActiveTab('prospects');
      else setActiveTab('customers');
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  if (isLoading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="https://customer-assets.emergentagent.com/job_steel-build-pro/artifacts/imjdxz67_3d-metal-home-building-logo%202.jpg" alt="Logo" className="h-16 w-auto" />
            <div><h1 className="text-lg font-bold" style={{ fontFamily: 'Rajdhani, sans-serif' }}>3D METAL HOME BUILDING</h1><p className="text-xs text-slate-600">CRM Dashboard</p></div>
          </div>
          <button onClick={handleLogout} className="flex items-center space-x-2 text-slate-600 hover:text-orange-500"><LogOut size={20} /><span>Logout</span></button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6">
          <nav className="flex space-x-4 overflow-x-auto">
            {['overview', 'leads', 'prospects', 'customers', 'contacts', 'invoices', 'commissions', 'email', 'visitors', 'settings', 'chats'].map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`py-4 px-2 border-b-2 font-medium text-sm uppercase tracking-wider ${activeTab === tab ? 'border-orange-500 text-orange-500' : 'border-transparent text-slate-600'}`} style={{ fontFamily: 'Rajdhani, sans-serif' }} data-testid={`tab-${tab}`}>{tab}</button>
            ))}
          </nav>
        </div>
      </div>

      <div className="p-6">
        {/* Overview */}
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <StatCard icon={<UserPlus className="text-blue-500" size={24} />} value={stats?.leads || 0} label="Leads" />
              <StatCard icon={<Users className="text-purple-500" size={24} />} value={stats?.prospects || 0} label="Prospects" />
              <StatCard icon={<Users className="text-orange-500" size={24} />} value={stats?.customers || 0} label="Customers" />
            </div>
            <div className="bg-white border p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Sales Pipeline</h3>
              <div className="flex items-center justify-center space-x-4">
                <PipelineStep value={stats?.leads || 0} label="Leads" color="blue" />
                <ArrowRight className="text-slate-400" />
                <PipelineStep value={stats?.prospects || 0} label="Prospects" color="purple" />
                <ArrowRight className="text-slate-400" />
                <PipelineStep value={stats?.customers || 0} label="Customers" color="orange" />
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-white border p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => { setEditingLead(null); setShowLeadModal(true); }} className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded flex items-center justify-center space-x-2"><Plus size={18} /><span>Add Lead</span></button>
                  <button onClick={() => { setEditingProspect(null); setShowProspectModal(true); }} className="bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded flex items-center justify-center space-x-2"><Plus size={18} /><span>Add Prospect</span></button>
                  <button onClick={() => { setEditingCustomer(null); setShowCustomerModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded flex items-center justify-center space-x-2"><Plus size={18} /><span>Add Customer</span></button>
                </div>
              </div>
              <div className="bg-white border p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span>Total in Pipeline</span><span className="font-bold">{(stats?.leads || 0) + (stats?.prospects || 0) + (stats?.customers || 0)}</span></div>
                  <div className="flex justify-between"><span>Templates Created</span><span className="font-bold">{templates.length}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Leads</h2>
            <div className="bg-white border rounded-lg mb-6">
              <div className="p-4">
                <div className="flex justify-end mb-4">
                  <button onClick={() => { setEditingLead(null); setShowLeadModal(true); }} className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 flex items-center space-x-2"><Plus size={20} /><span>Add Lead</span></button>
                </div>
                {leads.length > 0 ? (
                  <div className="space-y-4">
                    {leads.map((lead) => (
                      <ExpandableEntityCard
                        key={lead.id}
                        entity={lead}
                        type="lead"
                        color="blue"
                        onEdit={() => { setEditingLead(lead); setShowLeadModal(true); }}
                        onDelete={() => handleDelete('lead', lead.id)}
                        onConvert={() => handleConvert('lead', lead.id, 'prospect')}
                        convertLabel="Convert to Prospect"
                       
                        onSave={fetchData}
                        templates={templates}
                      />
                    ))}
                  </div>
                ) : <div className="text-center py-12 text-slate-500">No leads yet. Click "Add Lead" to create one.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Prospects Tab */}
        {activeTab === 'prospects' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Prospects</h2>
            <div className="bg-white border rounded-lg mb-6">
              <div className="p-4">
                <div className="flex justify-end mb-4">
                  <button onClick={() => { setEditingProspect(null); setShowProspectModal(true); }} className="bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 flex items-center space-x-2"><Plus size={20} /><span>Add Prospect</span></button>
                </div>
                {prospects.length > 0 ? (
                  <div className="space-y-4">
                    {prospects.map((prospect) => (
                      <ExpandableEntityCard
                        key={prospect.id}
                        entity={prospect}
                        type="prospect"
                        color="purple"
                        onEdit={() => { setEditingProspect(prospect); setShowProspectModal(true); }}
                        onDelete={() => handleDelete('prospect', prospect.id)}
                        onConvert={() => handleConvert('prospect', prospect.id, 'customer')}
                        convertLabel="Convert to Customer"
                       
                        onSave={fetchData}
                        showRating
                        templates={templates}
                      />
                    ))}
                  </div>
                ) : <div className="text-center py-12 text-slate-500">No prospects yet. Click "Add Prospect" to create one.</div>}
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Customers</h2>
              <button onClick={() => { setEditingCustomer(null); setShowCustomerModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 flex items-center space-x-2"><Plus size={20} /><span>Add Customer</span></button>
            </div>
            {customers.length > 0 ? (
              <div className="space-y-4">
                {customers.map((customer) => (
                  <ExpandableEntityCard
                    key={customer.id}
                    entity={customer}
                    type="customer"
                    color="orange"
                    onEdit={() => { setEditingCustomer(customer); setShowCustomerModal(true); }}
                    onDelete={() => handleDelete('customer', customer.id)}
                   
                    onSave={fetchData}
                    showRating
                    templates={templates}
                  />
                ))}
              </div>
            ) : <div className="bg-white border p-12 rounded-lg text-center text-slate-500">No customers yet.</div>}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Email Templates</h2>
              <button onClick={() => { setEditingTemplate(null); setShowTemplateModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 flex items-center space-x-2"><Plus size={20} /><span>Add Template</span></button>
            </div>
            <div className="grid gap-4">
              {templates.map((t) => (
                <div key={t.id} className="bg-white border p-6 rounded-lg">
                  <div className="flex justify-between items-start mb-4">
                    <div><h3 className="text-lg font-semibold">{t.name}</h3><p className="text-sm text-slate-600">Subject: {t.subject}</p></div>
                    <div className="flex space-x-2">
                      <button onClick={() => { setEditingTemplate(t); setShowTemplateModal(true); }} className="text-orange-500"><Edit size={18} /></button>
                      <button onClick={() => handleDelete('template', t.id)} className="text-red-500"><Trash2 size={18} /></button>
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded text-sm whitespace-pre-wrap">{t.body}</div>
                </div>
              ))}
              {templates.length === 0 && <div className="bg-white border p-12 rounded-lg text-center text-slate-600">No templates yet.</div>}
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <InvoicesTab 
            
            invoices={invoices}
            invoiceSummary={invoiceSummary}
            customers={customers}
            salesReps={salesReps}
            onSave={fetchData}
          />
        )}

        {/* Contact Form Submissions Tab */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Contact Form Submissions</h2>
            <div className="bg-white border rounded-lg">
              {contacts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Name</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Company</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Email</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Phone</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Type</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Job Title</th>
                        <th className="text-left p-4 font-semibold text-slate-700">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact, index) => (
                        <tr key={contact.id || index} className="border-b hover:bg-slate-50">
                          <td className="p-4 text-sm text-slate-600">{new Date(contact.timestamp).toLocaleDateString()}</td>
                          <td className="p-4 font-medium">{contact.first_name} {contact.last_name}</td>
                          <td className="p-4">{contact.company_name}</td>
                          <td className="p-4"><a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a></td>
                          <td className="p-4"><a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">{contact.phone}</a></td>
                          <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-medium ${contact.business_type === 'home' ? 'bg-green-100 text-green-700' : contact.business_type === 'commercial' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{contact.business_type}</span></td>
                          <td className="p-4">{contact.job_title}</td>
                          <td className="p-4 max-w-xs truncate" title={contact.comments}>{contact.comments || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center text-slate-600">No contact form submissions yet.</div>
              )}
            </div>
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <CommissionsTab 
            
            salesReps={salesReps} 
            commissions={commissions} 
            commissionTiers={commissionTiers}
            commissionSummary={commissionSummary}
            commissionChartData={commissionChartData}
            customers={customers}
            onSave={fetchData}
          />
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <EmailTab 
           
            leads={leads}
            prospects={prospects}
            customers={customers}
            emailLogs={emailLogs}
            emailTemplates={emailTemplates}
            onSave={fetchData}
          />
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <VisitorsTab />
        )}

        {/* Settings Tab - Field Manager */}
        {activeTab === 'settings' && (
          <FieldManager customFields={customFields} customSections={customSections} onSave={fetchData} />
        )}

        {/* Chats Tab */}
        {activeTab === 'chats' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Chat Sessions</h2>
            <div className="grid gap-4">
              {chats.map((chat) => (
                <div key={chat.session_id} className="bg-white border p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">Session: {chat.session_id.substring(0, 16)}...</h3>
                  <p className="text-sm text-slate-600 mb-4">{chat.message_count} messages</p>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {chat.messages.map((m) => (
                      <div key={m.timestamp} className="border-l-2 border-orange-500 pl-4 py-2">
                        <p className="text-xs text-slate-500">{new Date(m.timestamp).toLocaleString()}</p>
                        <p className="text-sm font-medium">Q: {m.message}</p>
                        <p className="text-sm text-slate-600">A: {m.response}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {chats.length === 0 && <div className="bg-white border p-12 rounded-lg text-center text-slate-600">No chats yet.</div>}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLeadModal && <LeadModal lead={editingLead} onClose={() => { setShowLeadModal(false); setEditingLead(null); }} onSave={() => { fetchData(); setShowLeadModal(false); setEditingLead(null); }} customFields={customFields} customSections={customSections} />}
      {showProspectModal && <ProspectModal prospect={editingProspect} onClose={() => { setShowProspectModal(false); setEditingProspect(null); }} onSave={() => { fetchData(); setShowProspectModal(false); setEditingProspect(null); }} customFields={customFields} customSections={customSections} />}
      {showCustomerModal && <CustomerModal customer={editingCustomer} onClose={() => { setShowCustomerModal(false); setEditingCustomer(null); }} onSave={() => { fetchData(); setShowCustomerModal(false); setEditingCustomer(null); }} customFields={customFields} customSections={customSections} />}
      {showJobModal && <JobModal job={editingJob} customers={customers} onClose={() => { setShowJobModal(false); setEditingJob(null); }} onSave={() => { fetchData(); setShowJobModal(false); setEditingJob(null); }} />}
      {showCallbackModal && <CallbackModal callback={editingCallback} customers={customers} onClose={() => { setShowCallbackModal(false); setEditingCallback(null); }} onSave={() => { fetchData(); setShowCallbackModal(false); setEditingCallback(null); }} />}
      {showTemplateModal && <TemplateModal template={editingTemplate} onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }} onSave={() => { fetchData(); setShowTemplateModal(false); setEditingTemplate(null); }} />}
    </div>
  );
};

// Helper Components
const StatCard = ({ icon, value, label }) => (
  <div className="bg-white border p-4 rounded-lg">
    <div className="flex items-center justify-between mb-2">{icon}<span className="text-2xl font-bold">{value}</span></div>
    <p className="text-xs text-slate-600 font-medium">{label}</p>
  </div>
);

const PipelineStep = ({ value, label, color }) => (
  <div className="text-center">
    <div className={`bg-${color}-100 text-${color}-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2`}><span className="text-xl font-bold">{value}</span></div>
    <p className="text-sm font-medium">{label}</p>
  </div>
);

export default AdminDashboard;
